import React, { useCallback, useEffect, useState } from "react";
import DataProvider from "../../apis";
import type { DoctorPrescriptionItem } from "../../apis";
import { Image, Card, Tag, Pagination, Spin, Input, Empty, Button } from "antd";
import { useNavigate } from "react-router-dom";
import { SearchOutlined, UserOutlined, FileTextOutlined } from "@ant-design/icons";
import Colors from "../../constants/Colors";
import {
  doctorPrescriptionStatusConfig,
  E_PRESCRIPTIONS_BASE,
} from "../../constants/e-prescriptions";

export default function Eprescriptionlist() {
  const [data, setData] = useState<DoctorPrescriptionItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize] = useState(12);
  const [searchName, setSearchName] = useState("");

  const fetchData = useCallback(
    async (page: number = 1) => {
      setLoading(true);
      try {
        const res = await DataProvider.getDoctorPrescriptionsQuery(
          page,
          pageSize,
          searchName ? { customerName: searchName } : undefined
        );
        const result = res.data as {
          doctorPrescriptions: DoctorPrescriptionItem[];
          totalPages: number;
        };
        setData(result.doctorPrescriptions ?? []);
        setTotalPages(result.totalPages ?? 1);
      } catch (error) {
        console.error("Error fetching doctor prescriptions:", error);
        setData([]);
      } finally {
        setLoading(false);
      }
    },
    [pageSize, searchName]
  );

  useEffect(() => {
    fetchData(pageNumber);
  }, [fetchData, pageNumber]);

  const handleSearch = () => {
    setPageNumber(1);
    fetchData(1);
  };

  const navigate = useNavigate();

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Input.Search
          placeholder="Search by patient name or order contact"
          allowClear
          onSearch={handleSearch}
          onChange={(e) => setSearchName(e.target.value)}
          className="max-w-sm"
          aria-label="Search prescriptions by patient name"
          prefix={<SearchOutlined className="text-gray-400" />}
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Spin size="large" tip="Loading prescriptions…" />
        </div>
      ) : data.length > 0 ? (
        <>
          <p className="text-sm text-gray-500 m-0">
            Each card shows the{" "}
            <span className="font-medium text-gray-700">order status</span>{" "}
            (whether the medication order has been served to the patient). Tap a card for full
            details.
          </p>
          <ul className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 list-none m-0 p-0">
            {data.map((item) => {
              const status = doctorPrescriptionStatusConfig[item.status] ?? {
                color: "#94a3b8",
                label: item.status?.replace(/_/g, " ") ?? "—",
                servedHint: undefined,
              };
              return (
                <li key={item.id}>
                  <Card
                    hoverable
                    onClick={() => navigate(`${E_PRESCRIPTIONS_BASE}/${item.id}`)}
                    className="h-full rounded-lg border-gray-100 shadow-sm hover:shadow-md transition-shadow"
                    styles={{ body: { padding: 16 } }}
                  >
                    <div className="flex gap-4">
                      <div className="shrink-0">
                        {item.image ? (
                          <Image
                            width={64}
                            height={64}
                            src={item.image}
                            alt=""
                            className="object-cover rounded-lg"
                            fallback="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='64' height='64' viewBox='0 0 64 64' fill='%23e5e7eb'%3E%3Crect width='64' height='64' rx='8' fill='%23f3f4f6'/%3E%3Cpath d='M20 28h24v2H20zm0 6h24v2H20zm0 6h16v2H20z' fill='%239ca3af'/%3E%3C/svg%3E"
                          />
                        ) : (
                          <div
                            className="w-16 h-16 rounded-lg bg-gray-100 flex items-center justify-center"
                            aria-hidden
                          >
                            <FileTextOutlined className="text-2xl text-gray-400" />
                          </div>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-gray-900 m-0 truncate flex items-center gap-1">
                          <UserOutlined className="text-gray-400 text-xs shrink-0" />
                          {item.patientName}
                        </p>
                        <p className="text-xs text-gray-500 mt-1 mb-1 font-mono">
                          Order #{item.orderId}
                        </p>
                        <p className="text-xs text-gray-500 m-0 mb-2">Order status</p>
                        <Tag color={status.color} className="text-xs rounded">
                          {status.label}
                        </Tag>
                        {status.servedHint && (
                          <p className="text-xs text-gray-500 mt-1.5 m-0 leading-snug">
                            {status.servedHint}
                          </p>
                        )}
                        {item.timestamp && (
                          <p className="text-xs text-gray-400 mt-2 m-0">
                            {new Date(item.timestamp).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    </div>
                  </Card>
                </li>
              );
            })}
          </ul>
          {totalPages > 1 && (
            <div className="flex justify-center pt-4">
              <Pagination
                current={pageNumber}
                total={totalPages * pageSize}
                pageSize={pageSize}
                onChange={(page) => setPageNumber(page)}
                showSizeChanger={false}
                aria-label="Prescription list pagination"
              />
            </div>
          )}
        </>
      ) : (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <span className="text-gray-500">
              No prescriptions yet. Create one for a patient and we’ll handle
              fulfillment and delivery to them.
            </span>
          }
          className="py-12"
        >
          <Button
            type="primary"
            onClick={() => navigate(`${E_PRESCRIPTIONS_BASE}/new`)}
            style={{ background: Colors.primary }}
            aria-label="Create first e-prescription"
          >
            Create e-prescription
          </Button>
        </Empty>
      )}
    </div>
  );
}
